import { useCallback, useRef } from 'react'

import { useIncidentsBroadCastChannelReceiver } from '@/hooks/useOnNotification'
import { useOpenIncident } from '@/hooks/useOpenIncident'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { useNextUpIncidentQuery } from '@/graphql/generated/hooks'
import {
  IncidentFilterInput,
  IncidentPriority,
  IncidentStatus,
} from '@/graphql/generated/schemas'
import { useAppSelector } from '@/redux/hooks'
import { extendMutedFilter } from '@/redux/incidents/conversion'
import {
  selectIncidentFilterCount,
  selectIncidentsFilterInput,
} from '@/redux/incidents/incidentsSlice'
import { selectMe } from '@/redux/me/meSlice'
import { trackEvent } from '@/utils/analytics'
import { tVar } from '@/utils/i18n'

import { PRIORITY_QUEUE_ANALYTICS } from '../constants/constants'
import { getPriorityQueueOrderBy } from '../utils/query'

interface UseNextUpIncidentIProps {
  // Gates both queries. The button is only mounted while Priority Queue is
  // active, but this keeps the hook self-contained and testable.
  isEnabled?: boolean
}

interface NextUpTargetI {
  incidentId: string
  displayId: string
  priority?: IncidentPriority | null
  queueDepth: number
  isResume: boolean
}

interface UseNextUpIncidentResultIProps {
  label: string
  isResume: boolean
  isDisabled: boolean
  isLoading: boolean
  onPress: () => void
}

// Next Up always means "oldest qualifying incident", independent of whatever
// direction the operator currently has the table's Date column flipped to — so
// this is hardcoded ascending, not derived from table state.
const NEXT_UP_ORDER_BY = getPriorityQueueOrderBy(false)

// Backend pagination is non-standard here: `first` is the page index and
// `last` is the page size (see useIncidentsPage.ts). Page 0, size 1 therefore
// returns the single top row of the ordered connection.
const FIRST_PAGE = 0
const SINGLE_ROW = 1

type NextUpNode = {
  id: string
  displayId: string
  priority?: IncidentPriority | null
}

const toTarget = (
  node: NextUpNode,
  totalCount: number | null | undefined,
  isResume: boolean
): NextUpTargetI => ({
  incidentId: node.id,
  displayId: node.displayId,
  priority: node.priority,
  queueDepth: totalCount ?? 0,
  isResume,
})

interface ResolveTargetIProps {
  resumeIncident?: NextUpNode | null
  resumeTotalCount?: number | null
  isResumeFetching: boolean
  nextUpIncident?: NextUpNode | null
  nextUpTotalCount?: number | null
}

/**
 * Picks the Next Up/Resume target, enforcing resume-first (ENG-31858):
 * 1. The operator's own in-progress incident always wins ("Resume").
 * 2. While Resume is still fetching, hold at no target — a stale-empty cache
 *    must not surface a Next Up the network Resume response might override.
 * 3. Only once Resume has settled with nothing to resume does Next Up apply.
 */
const resolveTarget = ({
  resumeIncident,
  resumeTotalCount,
  isResumeFetching,
  nextUpIncident,
  nextUpTotalCount,
}: ResolveTargetIProps): NextUpTargetI | undefined => {
  if (resumeIncident) return toTarget(resumeIncident, resumeTotalCount, true)
  if (isResumeFetching) return undefined
  if (nextUpIncident) return toTarget(nextUpIncident, nextUpTotalCount, false)
  return undefined
}

const getNextUpLabel = (t: TFunction<any>, target?: NextUpTargetI): string => {
  if (!target) return t('incidents:priority_queue_next_up')

  return tVar(
    t,
    target.isResume
      ? 'incidents:priority_queue_resume_with_id'
      : 'incidents:priority_queue_next_up_with_id',
    { displayId: target.displayId }
  )
}

// The Resume and Next Up lookups are the same query — fetch the single top row
// (page 0, size 1) of the oldest-first ordered connection — differing only in
// their filter and skip. Wrapping them keeps that shared config in one place.
const useSingleIncidentQuery = (filter: IncidentFilterInput, skip: boolean) =>
  useNextUpIncidentQuery({
    fetchPolicy: 'cache-and-network',
    skip,
    variables: {
      filter,
      first: FIRST_PAGE,
      last: SINGLE_ROW,
      orderBy: NEXT_UP_ORDER_BY,
    },
  })

/**
 * Resolves the Next Up/Resume target for the Priority Queue: the operator's own
 * highest-priority, oldest In Progress incident if they have one ("Resume"),
 * else the highest-priority, oldest unacknowledged incident ("Next Up"). Both
 * queries respect the currently active table filters and are routed through
 * `extendMutedFilter`, same as the main incidents query, so Next Up never
 * recommends an incident the table itself is hiding as muted. Opening the
 * target opens the drawer only — no auto-claim/acknowledge, per ENG-30964.
 */
export const useNextUpIncident = ({
  isEnabled = true,
}: UseNextUpIncidentIProps = {}): UseNextUpIncidentResultIProps => {
  const { t } = useTranslation()
  const me = useAppSelector(selectMe)
  const activeFilters = useAppSelector(selectIncidentsFilterInput)
  const activeFilterCount = useAppSelector(selectIncidentFilterCount)
  const { openIncident } = useOpenIncident()

  const resumeFilter: IncidentFilterInput = {
    ...extendMutedFilter(activeFilters),
    statusIn: [IncidentStatus.InProgress],
    ownerId: me?.id,
  }

  // "Resume": the operator's own oldest, highest-priority In Progress incident.
  const {
    data: resumeData,
    loading: isResumeFetching,
    refetch: refetchResume,
  } = useSingleIncidentQuery(resumeFilter, !isEnabled || !me?.id)

  const resumeIncident = resumeData?.incidents?.edges?.[0]?.node
  const hasResumeIncident = Boolean(resumeIncident)

  // No ownerId override here: Priority Queue now clears extraFilterCriteria.ownerId
  // at the state level (ENG-32284), so activeFilters never carries an owner scope
  // while this fallback query can run.
  const nextUpFilter: IncidentFilterInput = {
    ...extendMutedFilter(activeFilters),
    statusIn: [IncidentStatus.Active],
  }

  // "Next Up": the oldest, highest-priority unacknowledged (Active) incident.
  // Only runs when the operator has no In Progress incident to resume.
  const {
    data: nextUpData,
    loading: isNextUpFetching,
    refetch: refetchNextUp,
  } = useSingleIncidentQuery(
    nextUpFilter,
    // Gate on the *raw* fetching state (not a cached-suppressed one) and on
    // me?.id: Next Up must stay skipped until Resume has settled, or a
    // stale-empty cached Resume result could let Next Up run before the network
    // confirms there's no in-progress incident to resume (resume-first,
    // ENG-31858).
    !isEnabled || !me?.id || isResumeFetching || hasResumeIncident
  )

  const nextUpIncident = nextUpData?.incidents?.edges?.[0]?.node

  // Keep Next Up in lockstep with the main table: a resolve (or any incident
  // change) arrives on the shared, throttled broadcast channel, so re-run the
  // active query — an incident resolved elsewhere then drops out on refresh.
  //
  // The logic lives in a ref so the receiver gets a *stable* callback. A callback
  // keyed on hasResumeIncident (etc.) would change identity when the resume
  // result flips, re-running the receiver effect — which closes the channel and
  // clears any pending 5s-throttle timer, silently dropping a queued refetch.
  const refetchLatest = useRef<() => void>()
  refetchLatest.current = () => {
    // refetch() bypasses skip, so bail while disabled or before the user is
    // known — otherwise refetchResume() would run with ownerId: undefined.
    if (!isEnabled || !me?.id) return
    refetchResume()
    // Only refetch Next Up when it's actually the active query. It's skipped
    // both while a Resume target exists and while Resume is still fetching;
    // refetch() bypasses skip, so mirror that to avoid a wasted request.
    if (!hasResumeIncident && !isResumeFetching) refetchNextUp()
  }

  const refetchTarget = useCallback(() => refetchLatest.current?.(), [])

  useIncidentsBroadCastChannelReceiver(refetchTarget)

  const target = resolveTarget({
    resumeIncident,
    resumeTotalCount: resumeData?.incidents?.totalCount,
    isResumeFetching,
    nextUpIncident,
    nextUpTotalCount: nextUpData?.incidents?.totalCount,
  })

  const isResume = Boolean(target?.isResume)
  const label = getNextUpLabel(t, target)

  const onPress = () => {
    if (!target) return

    trackEvent(PRIORITY_QUEUE_ANALYTICS.nextUpPressed, {
      incident_id: target.incidentId,
      incident_display_id: target.displayId,
      priority: target.priority,
      queue_depth: target.queueDepth,
      active_filters: activeFilterCount,
      resolution_path: target.isResume ? 'in_progress' : 'unacknowledged',
    })
    openIncident({
      incidentId: target.incidentId,
      displayId: target.displayId,
    })
  }

  return {
    label,
    isResume,
    isDisabled: !target,
    // Spinner only while still determining with nothing confirmed to show, so a
    // cached target (Resume or Next Up) never flashes a spinner on revalidation.
    isLoading: !target && (isResumeFetching || isNextUpFetching),
    onPress,
  }
}
